/**
 * Unified type-picker form.
 *
 * Mounted by `EncounterTypeDispatcher` when the destination encounter type
 * cannot be resolved deterministically (free creation, multi-element
 * `restrictToEncounterTypes`). Renders a `SteppedForm` whose only step is
 * the encounter-type picker, so the modal shows the wizard chrome
 * immediately rather than a bare card grid.
 *
 * On pick the form remounts the resolved typed form (via the dispatcher's
 * internal `ResolveTypedForm` switch) by passing a single-element
 * `restrictToEncounterTypes` downstream. The typed form's own `type` step
 * is then hidden (its `showWhen` collapses when the restriction list has
 * one element, when an `encounter` is being edited, or when an activity
 * fixes the type).
 *
 * Internally the component is split into two render paths so exactly one
 * descendant writes to the forwarded `EncounterFormRef` per render:
 *  - {@link PickerOnlyForm} owns the no-op imperative handle while the
 *    user is still on the picker step.
 *  - `ResolveTypedForm` owns the real imperative handle once a type is
 *    picked.
 *
 * Without that split, both descendants would call `useImperativeHandle`
 * on the same forwarded ref and the parent's no-op could win during a
 * commit where the typed form's handle is deferred behind a conditional
 * (loading skeleton, suspense, errored hydration).
 *
 * @agentic connections
 */

import { Skeleton, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { forwardRef, useImperativeHandle, useState } from "react";
import type { Encounter } from "@medplum/fhirtypes";
import type { AppliedBusinessRule } from "@benchmark/core/resolvers";

import { FormStep, SteppedForm } from "@benchmark/ui/components/atomic";
import type { EncounterActivityContext } from "@benchmark/core/types";

import { EncounterTypePicker } from "./EncounterTypePicker";
import type { EncounterFormRef } from "./EncounterFormShell";
import { ResolveTypedForm } from "./resolveTypedForm";

/**
 * Props accepted by `UnifiedTypePickerForm`. Mirrors the dispatcher's prop
 * bag so the resolved typed form receives the same configuration once a
 * type is picked.
 *
 * @agentic connections
 */
export type UnifiedTypePickerFormProps = {
  patientId?: string;
  encounter?: Encounter;
  onSuccess?: (encounterId: string) => void;
  onCancel?: () => void;
  activityContext?: EncounterActivityContext;
  restrictToEncounterTypes?: string[];
  nextActivityLabel?: string;
  nextActivityDefaultOwner?: { ref: string; display: string };
  nextTaskOwnerOverride?: { ref: string; display: string } | null;
  onNextTaskOwnerOverrideChange?: (
    owner: { ref: string; display: string } | null,
  ) => void;
  nextActivityAppliedRule?: AppliedBusinessRule;
  isResolvingNextTaskOwner?: boolean;
};

/**
 * Internal form shape for the unified picker. Only the type discriminator
 * is tracked; once a type is picked, the dispatcher remounts the resolved
 * typed form which initialises its own `useForm`.
 *
 * @agentic connections
 */
type UnifiedPickerFormValues = {
  "encounter-type": string;
};

/**
 * Props for {@link PickerOnlyForm}. The picker render path needs only the
 * patient id, the optional allow-list, and the pick callback. It does not
 * need the rest of the dispatcher's prop bag (those are forwarded to the
 * typed form once a type is picked).
 *
 * @agentic connections
 */
type PickerOnlyFormProps = {
  patientId: string;
  restrictToEncounterTypes?: string[];
  onPick: (type: string) => void;
};

/**
 * Picker-only render path of `UnifiedTypePickerForm`. Owns the no-op
 * imperative handle so that, until a type is picked, `ModalProvider`
 * sees a `checkProtection` that always allows close — the picker has
 * no editable user state to protect. Once a type is picked, the parent
 * re-renders to mount `ResolveTypedForm`, which becomes the sole
 * `EncounterFormRef` writer.
 *
 * @agentic connections
 */
const PickerOnlyForm = forwardRef<EncounterFormRef, PickerOnlyFormProps>(
  function PickerOnlyForm(
    { patientId, restrictToEncounterTypes, onPick },
    ref,
  ) {
    const form = useForm<UnifiedPickerFormValues>({
      initialValues: { "encounter-type": "" },
    });

    useImperativeHandle(
      ref,
      () => ({
        checkProtection: async () => true,
        markAsClean: () => undefined,
      }),
      [],
    );

    return (
      <div data-testid="unified-type-picker-form">
        <SteppedForm
          form={form}
          steps={[
            {
              id: "type",
              title: "Type",
              subtitle: "Select the type of interaction.",
              requiredFields: ["encounter-type"],
            },
          ]}
          onSubmit={() => undefined}
          showNavigation={false}
          showCancel={false}
        >
          <FormStep id="type">
            <EncounterTypePicker
              patientId={patientId}
              allowedTypes={restrictToEncounterTypes}
              onPick={onPick}
            />
          </FormStep>
        </SteppedForm>
      </div>
    );
  },
);

/**
 * Unified type-picker form. Routes between the picker step and the
 * resolved typed form so that exactly one descendant owns the forwarded
 * `EncounterFormRef` on any given render — see file header for the
 * ordering hazard this avoids.
 *
 * @agentic connections
 */
export const UnifiedTypePickerForm = forwardRef<
  EncounterFormRef,
  UnifiedTypePickerFormProps
>(function UnifiedTypePickerForm(props, ref) {
  const { patientId, restrictToEncounterTypes } = props;
  const [pickedType, setPickedType] = useState<string | undefined>(undefined);

  if (pickedType) {
    return (
      <ResolveTypedForm
        ref={ref}
        pickedType={pickedType}
        {...props}
        restrictToEncounterTypes={[pickedType]}
      />
    );
  }

  if (!patientId) {
    return (
      <div data-testid="unified-type-picker-form-loading">
        <Stack gap="md">
          <Skeleton height={32} width="40%" />
          <Skeleton height={120} />
        </Stack>
      </div>
    );
  }

  return (
    <PickerOnlyForm
      ref={ref}
      patientId={patientId}
      restrictToEncounterTypes={restrictToEncounterTypes}
      onPick={setPickedType}
    />
  );
});
